<template>
  <template v-if="!isOnline">
    <Connection v-if="showConnection" />
  </template>

  <Navbar v-if="showNavbar"></Navbar>

  <sidebar v-if="showSidebar"></sidebar>

  <main class="main-content max-height-vh-100 h-100" ref="mainContent" :style="showNavbar ? 'padding-top: 67px' : ''">
    <router-view />
  </main>

  <ContactModal :open="isContactModalOpen" @close="closeContactModal" />

  <SessionTimeoutModal
    :open="showWarning"
    :secondsLeft="secondsLeft"
    @extend="extendSession"
    @logout="logoutNow"
  />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import Sidebar from "@/components/Sidebar.vue";
import Navbar from "@/components/Navbar.vue";
import Connection from "@/components/Connection.vue";
import ContactModal from "@/components/ContactModal.vue";
import SessionTimeoutModal from "@/components/Modals/SessionTimeoutModal.vue";
import { useContactModal } from "@/composables/useContactModal";
import { useInactivityTimer } from "@/composables/useInactivityTimer";
import { usePeppermintWidget } from "@/composables/usePeppermintWidget";

import axiosInstance from "./services/AxiosTokenInstance";
import { useAuthStore } from "@/stores";

const { isContactModalOpen, closeContactModal } = useContactModal();
const { showWarning, secondsLeft, extendSession, logoutNow } = useInactivityTimer();
usePeppermintWidget();

const route = useRoute();

const mainContent = ref(null);
const isOnline = ref(true);
let resizeObserver = null;

const currentRouteName = computed(() => route.name);

// Routes where no layout chrome (navbar, sidebar) should appear
const showConnection = computed(() => {
  const authRoutes = [
    "login",
    "login_passwordless",
    "login_passwordless_check",
    "self_registration",
  ];
  return !authRoutes.includes(currentRouteName.value);
});

const showNavbar = computed(() => {
  const hiddenRoutes = [
    "login",
    "login_passwordless",
    "login_passwordless_check",
    "self_registration",
    "agree2terms",
    "participant",
    "writeReport",
    "not-found",
  ];
  return !hiddenRoutes.includes(currentRouteName.value);
});

const showSidebar = computed(() => {
  const hiddenRoutes = [
    "login",
    "login_passwordless",
    "login_passwordless_check",
    "self_registration",
    "agree2terms",
    "participant",
    "survey",
    "taskLanguages",
    "writeReport",
    "not-found",
  ];
  return !hiddenRoutes.includes(currentRouteName.value);
});

// ResizeObserver with empty handler to suppress errors caused by
// password manager extensions (LastPass, 1Password, etc.) injecting
// elements into the page.
function initializeResizeObserver() {
  resizeObserver = new ResizeObserver(() => {});
  if (mainContent.value) {
    resizeObserver.observe(mainContent.value);
  }
}

async function getCurrentUser() {
  isOnline.value = true;
  const auth = useAuthStore();

  if (auth.user) {
    try {
      await axiosInstance.get("current_user");
    } catch (error) {
      if (error.response && error.response.status === 401) {
        await auth.logout();
      } else if (error.code === "ERR_NETWORK") {
        isOnline.value = false;
      }
    }
  }
}

// Ekvivalent za created() - direktno u setup
const interval = setInterval(getCurrentUser, 10000);

onMounted(() => {
  initializeResizeObserver();
});

onUnmounted(() => {
  resizeObserver?.disconnect();
  clearInterval(interval);
});
</script>
